import { useState } from 'react';
import { STATES } from '../data/states.js';
import { submitQuizPlayer } from '../utils/tracking.js';
import { emailWasSubmitted, markEmailSubmitted } from '../utils/storage.js';

const STATES_ALPHA = [...STATES].sort((a, b) => a.name.localeCompare(b.name));

export default function EmailCapture({ quizStats = {} }) {
  const [email, setEmail] = useState('');
  const [userState, setUserState] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [hidden, setHidden] = useState(emailWasSubmitted());
  const [unlock, setUnlock] = useState(null); // { referralsCount, threshold, cgoUnlocked } | null

  if (hidden) return null;

  async function handleSubmit() {
    if (!email || !email.includes('@')) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    try {
      const result = await submitQuizPlayer({
        email,
        optInToNewsletter: optIn,
        state: userState,
        quizStats,
      });
      setUnlock({
        referralsCount: result.referralsCount ?? 0,
        threshold: result.threshold ?? 3,
        cgoUnlocked: !!result.cgoUnlocked,
      });
      setStatus('success');
      markEmailSubmitted();
      // Leave the unlock card visible — don't auto-hide.
    } catch (e) {
      console.error(e);
      setStatus('error');
    }
  }

  return (
    <div className="email-capture fade-in delay-5">
      <img className="ed-horse" src="/ed-the-horse.png" alt="Ed the Horse, OpenEd's mascot" />
      {status === 'success' ? (
        <div className="success">
          <div>Thanks — look for it in your inbox. ✓</div>
          {unlock && (
            <div className="unlock-row">
              {unlock.cgoUnlocked ? (
                <span className="unlock-pill is-on">
                  Unlocked: {unlock.referralsCount} friends in
                </span>
              ) : (
                <span className="unlock-pill">
                  {unlock.referralsCount}/{unlock.threshold} friends —
                  invite {Math.max(0, unlock.threshold - unlock.referralsCount)} more to unlock
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <h3 className="capture-headline">Want to know how OpenEd works in your state?</h3>
          <div className="state-select-row">
            <select
              className="state-select"
              value={userState}
              onChange={e => setUserState(e.target.value)}
              aria-label="Your state"
            >
              <option value="">Pick your state</option>
              {STATES_ALPHA.map(s => (
                <option key={s.abbr} value={s.name}>
                  {s.name}{s.isOpenEd ? ' ★' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="email-capture-row">
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
              aria-label="Email address"
            />
            <button
              onClick={handleSubmit}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? '…' : 'Email me the details'}
            </button>
          </div>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={optIn}
              onChange={e => setOptIn(e.target.checked)}
            />
            <span>Also send me OpenEd Daily.</span>
          </label>
          <p className="state-legend">★ = OpenEd is active here.</p>
          {status === 'error' && (
            <p style={{ fontSize: '0.78rem', color: 'var(--incorrect)', marginTop: '0.5rem' }}>
              Something went wrong — please try again or check your email format.
            </p>
          )}
        </>
      )}
    </div>
  );
}
